import { ChevronDown, Text, Columns2, X, ChevronsUpDown, ChevronsDownUp, FileText } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { DeclarationSummary } from './DeclarationSummary';
import { useSelector, useDispatch } from 'react-redux';
import pdfIcon from "../../assets/pdf.png";
import PdfViewer from './PdfViewer';
import { scroller } from "react-scroll";
import { useLanguage, getReviewExtractedDataTranslation } from '../../hooks/useLanguage';
import { TemplateModal } from './TemplateModal';
import { setExtractedData, setEditableData, setSelectedTemplate, setDeclarationDirty, fetchTemplates, updateTabIsDirty, updateTabEditableData, updateWorkflowSessionState, addDocuments, setUploadedFiles } from '../../store/reducers';
import { getTemplateCSSClassFromColor } from '../../utils/templateColors';
import { convertFieldValue, NUMERIC_FIELDS } from '../../utils/templateUtils';
import { isAnyProcessingModalOpen } from '../../utils/processingModalUtils';
import { deepClone } from '../../utils/dataHelpers';
import { usePdfAssets } from '../../hooks/usePdfAssets';
import { getWorkflowDeclarations } from '../../utils/workflowDataUtils';
import { isActiveWorkflowProcessingBusyState } from '../../utils/workflowProcessingUiUtils';

const normalizeBinary = (val) => {
  if (Array.isArray(val)) return val[0] === 1 ? 1 : 0;
  if (val === null || val === undefined || val === '') return 0;
  const num = Number(val);
  return (!isNaN(num) && num === 1) ? 1 : 0;
};

export const ReviewExtractedData = () => {
  const dispatch = useDispatch();
  const cmrFile = useSelector((state) => state.documents.cmrFile);
  const exportFiles = useSelector((state) => state.documents.documents);
  
  // Get data from active tab if available, otherwise fallback to global state
  const activeTabId = useSelector(state => state.tabs?.activeTabId);
  const activeTabData = useSelector(state => {
    const tabsState = state.tabs;
    if (tabsState && tabsState.activeTabId && tabsState.tabs && tabsState.tabs[tabsState.activeTabId]) {
      return tabsState.tabs[tabsState.activeTabId];
    }
    return null;
  });

  const activeWorkflowSessionId = useSelector((state) => state.workflowSessions?.activeSessionId);
  const activeWorkflowSession = useSelector((state) => {
    const workflowSessions = state.workflowSessions;
    const activeId = workflowSessions?.activeSessionId;
    return activeId ? workflowSessions?.sessions?.[activeId] : null;
  });

  const isActiveWorkflowProcessingBusy = useSelector(isActiveWorkflowProcessingBusyState);

  const globalExtractedData = useSelector((state) => state.documents.extractedData);
  const globalEditableData = useSelector((state) => state.documents.editableData);

  const sessionState = activeWorkflowSession?.state;
  const sessionDeclarations = sessionState ? getWorkflowDeclarations(sessionState) : [];
  const hasPersistedWorkflowDeclarations =
    Array.isArray(sessionDeclarations) && sessionDeclarations.length > 0;

  // Per-workflow: once this workflow has saved declarations in session, never show another workflow's global documents slice
  const extractedData = hasPersistedWorkflowDeclarations
    ? (sessionState.extractedData != null
        ? (Array.isArray(sessionState.extractedData)
          ? sessionState.extractedData
          : [sessionState.extractedData])
        : sessionDeclarations)
    : globalExtractedData;

  const editableData = hasPersistedWorkflowDeclarations
    ? (sessionState.editableData != null
        ? (Array.isArray(sessionState.editableData)
          ? sessionState.editableData
          : [sessionState.editableData])
        : extractedData)
    : globalEditableData;

  const [files, setFiles] = useState([]);
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(true);
  const [selectedOption, setSelectedOption] = useState('text');
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileUrl, setSelectedFileUrl] = useState(null);

  useEffect(() => {
    if (isActiveWorkflowProcessingBusy && selectedOption === 'split') {
      setSelectedOption('text');
    }
  }, [isActiveWorkflowProcessingBusy, selectedOption]);

  const ocrInputID = useSelector((state) => {
    const activeId = state.workflowSessions?.activeSessionId;
    const activeSession = activeId ? state.workflowSessions?.sessions?.[activeId] : null;
    const st = activeSession?.state;
    if (st?.ocrInputID) {
      return String(st.ocrInputID).trim();
    }
    if (state.preparedJson?.ocrInputID) {
      return String(state.preparedJson.ocrInputID).trim();
    }
    const decls = st ? getWorkflowDeclarations(st) : [];
    if (decls.length > 0) {
      const firstItem = decls[0];
      const extractedOcrId = firstItem?.ocr_input_id || firstItem?.ocrInputId;
      if (extractedOcrId) {
        return String(extractedOcrId).trim();
      }
    }
    const globalExtracted = state.documents.extractedData;
    if (globalExtracted) {
      const dataArray = Array.isArray(globalExtracted) ? globalExtracted : [globalExtracted];
      if (dataArray.length > 0) {
        const firstItem = dataArray[0];
        const extractedOcrId = firstItem?.ocr_input_id || firstItem?.ocrInputId;
        if (extractedOcrId) {
          return String(extractedOcrId).trim();
        }
      }
    }
    return null;
  });
  
  // Use custom hook to fetch PDF assets from API
  const { fetchedPdfs, isLoadingPdfs } = usePdfAssets(selectedOption === 'split', ocrInputID);

  const [dropdownOpen, setDropdownOpen] = useState(false);

  const [isDirty, setIsDirty] = useState(false);
  const [hasOpenedTemplateModal, setHasOpenedTemplateModal] = useState(false);

  const [isSpanish] = useLanguage();

  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  
  // Get selectedTemplate from active workflow session state (already defined above)
  const selectedTemplate = activeWorkflowSession?.state?.selectedTemplate || null;
  const templates = useSelector((state) => {
    const templates = state.templates;
    // Ensure templates is always an array
    return Array.isArray(templates) ? templates : [];
  });
  
  // Track the last applied template to avoid infinite loops
  const lastAppliedTemplateRef = useRef(null);
  const hasAppliedPreferredTemplateRef = useRef(false);
  const prevWorkflowSessionIdRef = useRef(activeWorkflowSessionId);

  // Fetch templates from API on mount
  useEffect(() => {
    dispatch(fetchTemplates());
  }, [dispatch]);
  
  // Apply preferred template from preferences when workflow starts or extracted data is available
  useEffect(() => {
    const workflowChanged = prevWorkflowSessionIdRef.current !== activeWorkflowSessionId;
    
    // Reset flag when workflow changes
    if (workflowChanged) {
      hasAppliedPreferredTemplateRef.current = false;
      prevWorkflowSessionIdRef.current = activeWorkflowSessionId;
    }
    
    // Apply preferred template if no template selected, extractedData exists, templates loaded, and not already applied
    if (!selectedTemplate && 
        extractedData && // Only apply when extractedData exists (fresh data, not user-edited)
        templates.length > 0 && 
        !hasAppliedPreferredTemplateRef.current) {
      
      try {
        const storedPreferredTemplate = localStorage.getItem('preferredTemplate');
        if (storedPreferredTemplate) {
          const preferredTemplate = JSON.parse(storedPreferredTemplate);
          if (preferredTemplate && preferredTemplate.id) {
            // Find template in loaded templates (may have been updated)
            const matchingTemplate = templates.find(t => 
              String(t.id) === String(preferredTemplate.id) ||
              (t.title === preferredTemplate.title && t.id === preferredTemplate.id)
            );
            
            if (matchingTemplate) {
              // Don't set lastAppliedTemplateRef here - let useEffect handle applying template data
              hasAppliedPreferredTemplateRef.current = true;
              
              // CRITICAL: Save selectedTemplate to active workflow session state (per-workflow)
              // The useEffect at line 195 will detect this change and apply the template data automatically
              if (activeWorkflowSessionId) {
                dispatch(updateWorkflowSessionState(activeWorkflowSessionId, {
                  selectedTemplate: matchingTemplate,
                }));
              }
              
              // Also update global state for backward compatibility
              dispatch(setSelectedTemplate(matchingTemplate));
            } else {
              // Template not found in current templates, but mark as applied to avoid retrying
              hasAppliedPreferredTemplateRef.current = true;
            }
          }
        }
      } catch (e) {
        console.error('Error applying preferred template:', e);
        hasAppliedPreferredTemplateRef.current = true; // Mark as applied to avoid retrying
      }
    }
  }, [selectedTemplate, extractedData, templates, activeWorkflowSessionId, dispatch]);

  // Add/remove template class to body based on selected template color
  useEffect(() => {
    // Remove all template classes (from color palette)
    const allTemplateClasses = [
      'template-almeria', 'template-iberveg', 'template-actiture', 
      'template-zanobeet', 'template-cebollas', 'template-red', 
      'template-pink', 'template-gray', 'template-teal', 
      'template-orange', 'template-lime', 'template-indigo'
    ];
    document.body.classList.remove(...allTemplateClasses);
    
    // Add class for selected template based on its color
    if (selectedTemplate && selectedTemplate.color) {
      const cssClass = getTemplateCSSClassFromColor(selectedTemplate.color);
      if (cssClass) {
        document.body.classList.add(cssClass);
      }
    }

    // Cleanup: remove class when component unmounts or template changes
    return () => {
      document.body.classList.remove(...allTemplateClasses);
    };
  }, [selectedTemplate]);

  // Auto-apply template when selectedTemplate changes or extractedData becomes available
  useEffect(() => {
    // Re-apply if: template selected, data exists, template has data, and data changed (prevents infinite loops)
    
    if (selectedTemplate && 
        (editableData || extractedData) && 
        selectedTemplate.data &&
        typeof selectedTemplate.data === 'object') {
      
      // Check if template data has changed by comparing JSON stringified version
      const currentTemplateData = JSON.stringify(selectedTemplate.data);
      const lastAppliedTemplateData = lastAppliedTemplateRef.current 
        ? JSON.stringify(lastAppliedTemplateRef.current.data) 
        : null;
      
      // Check if template ID has changed (different template selected)
      const lastAppliedTemplateId = lastAppliedTemplateRef.current?.id;
      const currentTemplateId = selectedTemplate?.id;
      const templateIdChanged = lastAppliedTemplateId !== currentTemplateId;
      
      // Check if we have extractedData (fresh data) - prefer applying to extractedData over editableData
      const hasExtractedData = extractedData && (Array.isArray(extractedData) ? extractedData.length > 0 : true);
      
      // Check if we haven't applied this template yet (lastAppliedTemplateRef is null or different template)
      const hasNotAppliedThisTemplate = !lastAppliedTemplateRef.current || templateIdChanged;
      
      // Check if extractedData just arrived (template selected before extractedData was available)
      const extractedDataJustArrived = hasExtractedData && 
        (!lastAppliedTemplateRef.current || 
         (lastAppliedTemplateRef.current && !hasExtractedData));
      
      // Re-apply if template data changed, ID changed, not applied yet, or extractedData just arrived
      if (currentTemplateData === lastAppliedTemplateData && 
          !templateIdChanged && 
          !hasNotAppliedThisTemplate && 
          !extractedDataJustArrived) {
        return; // Skip re-application if template data unchanged and same template
      }
      
      // Update the ref to track this template version
      lastAppliedTemplateRef.current = selectedTemplate;
      
      // Get original data - prefer extractedData over editableData to avoid overwriting user edits
      let originalData = null;
      try {
        // Prefer extractedData (fresh data, not user-edited)
        if (extractedData) {
          originalData = extractedData;
        } else if (activeTabData && activeTabData.editableData) {
          originalData = activeTabData.editableData;
        } else if (editableData) {
          originalData = editableData;
        }
      } catch (e) {
        console.error('Error getting original data from tab:', e);
      }
      
      // Handle array/single object cases - prefer extractedData over editableData
      const currentData = extractedData || editableData;
      
      // Validate that we have data to apply template to
      if (!currentData) {
        console.warn('No data available to apply template to');
        return;
      }
      
      const dataArray = Array.isArray(currentData) ? currentData : [currentData];
      const originalDataArray = originalData 
        ? (Array.isArray(originalData) ? originalData : [originalData])
        : null;
      
      // CRITICAL: Ensure we have at least one declaration to apply template to
      if (dataArray.length === 0) {
        console.warn('No declarations found to apply template to');
        return;
      }
      
      const updatedData = dataArray.map((declaration, index) => {
        const updatedDeclaration = { ...declaration };
        
        // Find the original declaration by expediente or by index if expediente doesn't match
        let originalDeclaration = null;
        if (originalDataArray) {
          // First try to match by expediente
          originalDeclaration = originalDataArray.find(d => 
            d.expediente === declaration.expediente
          );
          // If no match by expediente, try by index
          if (!originalDeclaration && originalDataArray[index]) {
            originalDeclaration = originalDataArray[index];
          }
        }
        
        // Define all template-applicable fields Note: marcas is NOT included here - it should only be in embalajes, not at declaration level Start with hardcoded fields for backward compatibility and special handling
        const templateFields = {
          // Declaration type and dates
          tipo: selectedTemplate.data.tipo,
          fecha_registro: selectedTemplate.data.fecha_registro,
          fecha_creacion: selectedTemplate.data.fecha_creacion,
          fecha_operacion: selectedTemplate.data.fecha_operacion,
          mrn: selectedTemplate.data.mrn,
          
          // Declaration identification
          declaracion_1: selectedTemplate.data.declaracion_1,
          declaracion_2: selectedTemplate.data.declaracion_2,
          declaracion_3: selectedTemplate.data.declaracion_3,
          
          // Operators/Parties
          cliente_id: selectedTemplate.data.cliente_id,
          remitente_id: selectedTemplate.data.remitente_id,
          clave_declarante: selectedTemplate.data.clave_declarante,
          
          // Customs information
          aduana_entrada: selectedTemplate.data.aduana_entrada,
          aduana_salida: selectedTemplate.data.aduana_salida,
          aduana_despacho: selectedTemplate.data.aduana_despacho,
          
          // Transport information
          modo_transporte_frontera: selectedTemplate.data.modo_transporte_frontera,
          modo_transporte_interior: selectedTemplate.data.modo_transporte_interior,
          medio_transporte_frontera: selectedTemplate.data.medio_transporte || selectedTemplate.data.medio_transporte_frontera,
          identidad_transporte_frontera: selectedTemplate.data.identidad_transporte_frontera,
          
          // Country information
          pais_destino: selectedTemplate.data.pais_destino,
          pais_exportacion: selectedTemplate.data.pais_exportacion,
          pais_transporte_frontera: selectedTemplate.data.pais_transporte_frontera,
          pais_ubicacion: selectedTemplate.data.pais_ubicacion,
          
          // Location information
          localizacion: selectedTemplate.data.localizacion || selectedTemplate.data.localizacion_mercancia,
          tipo_ubicacion: selectedTemplate.data.tipo_ubicacion,
          provincia: selectedTemplate.data.provincia,
          codigo_un: selectedTemplate.data.codigo_un,
          lugar_entrega: selectedTemplate.data.lugar_entrega,
          tipo_autorizacion_cd: selectedTemplate.data.tipo_autorizacion_cd,
          identificacion_adicional: selectedTemplate.data.identificacion_adicional,
          
          // Currency information
          divisa: selectedTemplate.data.divisa,
          cambio_divisa: selectedTemplate.data.cambio_divisa,
          divisa_iso: selectedTemplate.data.divisa_iso,
          
          // Commercial information - normalize so we never store [0]
          tiene_contenedores: normalizeBinary(selectedTemplate.data.tiene_contenedores),
          total_bultos: selectedTemplate.data.total_bultos,
          factura: selectedTemplate.data.factura,
          fecha_factura: selectedTemplate.data.fecha_factura,
          condiciones_entrega: selectedTemplate.data.condiciones_entrega,
          naturaleza_transaccion: selectedTemplate.data.naturaleza_transaccion,
          
          // Contact information
          contacto_telefono: selectedTemplate.data.contacto_telefono,
          e_mail: selectedTemplate.data.e_mail,
          
          // Status and flags
          autorizacion_por_operacion: selectedTemplate.data.autorizacion_por_operacion,
          flag_datos_seguridad: selectedTemplate.data.flag_datos_seguridad,
          es_simplificado: selectedTemplate.data.es_simplificado,
          es_recapitulativo: selectedTemplate.data.es_recapitulativo,
          estado_pdc: selectedTemplate.data.estado_pdc,
          estado_edi: selectedTemplate.data.estado_edi,
          estado_edi_canarias: selectedTemplate.data.estado_edi_canarias,
          completo: selectedTemplate.data.completo,
          es_plantilla: selectedTemplate.data.es_plantilla,
          
          // Additional information
          observaciones: selectedTemplate.data.observaciones,
          
          // Item-level fields (regimen_solicitud is actually item-level, but kept for backward compatibility)
          regimen_solicitud: selectedTemplate.data.regimen_solicitud,
        };
        
        // Dynamically add all other fields from template.data (excludes nested structures: documentos, partidas, embalajes)
        const excludedKeys = ['documentos', 'partidas', 'embalajes', 'marcas'];
        Object.keys(selectedTemplate.data).forEach((key) => {
          // Skip if already in templateFields or is a special nested structure
          if (!excludedKeys.includes(key) && templateFields[key] === undefined) {
            templateFields[key] = selectedTemplate.data[key];
          }
        });
        
        // Helper function to validate and normalize numeric/boolean fields
        const normalizeNumericField = (value, fieldName, defaultValue) => {
          // If value is an array, null, or undefined, use default
          if (Array.isArray(value) || value === null || value === undefined || value === '') {
            return defaultValue;
          }
          // Convert to number if it's a valid number
          const numValue = Number(value);
          if (!isNaN(numValue) && isFinite(numValue)) {
            return numValue;
          }
          // If not a valid number, use default
          return defaultValue;
        };

        // Apply template fields: ALWAYS overwrite if exists in template, restore to original if not
        Object.keys(templateFields).forEach((key) => {
          // CRITICAL: Check if field exists in template.data (including null values - null is a valid value to apply) Only exclude undefined (field doesn't exist in template)
          const hasValueInTemplate = selectedTemplate.data.hasOwnProperty(key) && selectedTemplate.data[key] !== undefined;
          
          if (hasValueInTemplate) {
            // Field exists in template - ALWAYS overwrite with validation for numeric fields
            let valueToApply = templateFields[key];
            
            // Convert numeric fields using convertFieldValue (handles tipo and all other numeric fields)
            if (NUMERIC_FIELDS.has(key)) {
              valueToApply = convertFieldValue(key, valueToApply);
            } else {
              // Fallback to normalizeNumericField for legacy fields (can be removed later)
            if (key === 'flag_datos_seguridad') {
              valueToApply = normalizeNumericField(valueToApply, key, 0);
            } else if (key === 'completo') {
              valueToApply = normalizeNumericField(valueToApply, key, 1);
            } else if (key === 'es_plantilla') {
              valueToApply = normalizeNumericField(valueToApply, key, 0);
            } else if (key === 'es_simplificado' || key === 'es_recapitulativo' || key === 'autorizacion_por_operacion') {
              valueToApply = normalizeNumericField(valueToApply, key, 0);
              }
            }
            
            // ALWAYS overwrite - don't check if it already exists
            updatedDeclaration[key] = valueToApply;
          } else {
            // Field doesn't exist in template - restore to original value or clear
            const templateSpecificFields = [
              // Declaration type and dates
              'tipo', 'fecha_registro', 'fecha_creacion', 'fecha_operacion', 'mrn',
              // Declaration identification
              'declaracion_1', 'declaracion_2', 'declaracion_3',
              // Operators/Parties
              'cliente_id', 'remitente_id', 'clave_declarante',
              // Customs information
              'aduana_entrada', 'aduana_salida', 'aduana_despacho',
              // Transport information
              'modo_transporte_frontera', 'modo_transporte_interior', 'medio_transporte_frontera', 'identidad_transporte_frontera',
              // Country information
              'pais_destino', 'pais_exportacion', 'pais_transporte_frontera', 'pais_ubicacion',
              // Location information
              'tipo_ubicacion', 'localizacion', 'provincia', 'codigo_un', 'lugar_entrega', 'tipo_autorizacion_cd', 'identificacion_adicional',
              // Currency information
              'divisa', 'cambio_divisa', 'divisa_iso',
              // Commercial information
              'tiene_contenedores', 'total_bultos', 'factura', 'fecha_factura', 'condiciones_entrega', 'naturaleza_transaccion',
              // Contact information
              'contacto_telefono', 'e_mail',
              // Status and flags
              'autorizacion_por_operacion', 'flag_datos_seguridad', 'es_simplificado', 'es_recapitulativo', 'estado_pdc', 'estado_edi', 'estado_edi_canarias', 'completo', 'es_plantilla',
              // Additional information
              'observaciones',
              // Item-level (kept for backward compatibility)
              'regimen_solicitud',
            ];
            if (templateSpecificFields.includes(key)) {
              // Restore to original value from before templates were applied
              if (originalDeclaration && originalDeclaration[key] !== undefined) {
                // Also validate original value for numeric fields
                if (key === 'flag_datos_seguridad') {
                  updatedDeclaration[key] = normalizeNumericField(originalDeclaration[key], key, 0);
                } else if (key === 'completo') {
                  updatedDeclaration[key] = normalizeNumericField(originalDeclaration[key], key, 1);
                } else if (key === 'es_plantilla') {
                  updatedDeclaration[key] = normalizeNumericField(originalDeclaration[key], key, 0);
                } else {
                  updatedDeclaration[key] = originalDeclaration[key];
                }
              } else {
                // Set default values for numeric fields, empty string for others
                if (key === 'flag_datos_seguridad') {
                  updatedDeclaration[key] = 0;
                } else if (key === 'completo') {
                  updatedDeclaration[key] = 1;
                } else if (key === 'es_plantilla') {
                  updatedDeclaration[key] = 0;
                } else {
                  updatedDeclaration[key] = '';
                }
              }
            }
          }
        });
        
        // Handle documentos: add to partidas level, not declaration level In templates: codigo_documento is stored, and tipo is the numeric category (1,2,3,4) In the documents table: "Type" column shows codigo_documento
        if (selectedTemplate.data.documentos && Array.isArray(selectedTemplate.data.documentos) && selectedTemplate.data.documentos.length > 0) {
          const templateDocumentos = selectedTemplate.data.documentos.map((doc) => {
            // Handle backward compatibility: old templates use tipo as string ('N380'), new format uses codigo_documento + numeric tipo
            let codigoDoc = '';
            let tipoNum = 2; // Default: Documentos Suplementarios
            
            if (doc.codigo_documento) {
              // New format: codigo_documento exists
              codigoDoc = doc.codigo_documento;
              tipoNum = (doc.tipo !== undefined && doc.tipo !== null && typeof doc.tipo === 'number') ? doc.tipo : 2;
            } else if (doc.tipo && typeof doc.tipo === 'string') {
              // Old format: tipo is a string like 'N380', 'N730', etc.
              codigoDoc = doc.tipo;
              // Map old tipo string to numeric tipo
              if (codigoDoc === 'N730') {
                tipoNum = 4; // Documentos de Transporte
              } else if (codigoDoc === 'Y024') {
                tipoNum = 3; // Referencias Adicionales
              } else {
                tipoNum = 2; // Documentos Suplementarios (N380, N003, 1217, 1009, I5007, etc.)
              }
            } else if (doc.tipo && typeof doc.tipo === 'number') {
              // If tipo is already a number, use it (but codigo_documento should exist)
              tipoNum = doc.tipo;
            }
            
            return {
              codigo_documento: codigoDoc,
              tipo: tipoNum,
              numero: doc.numero || '',
              fecha: doc.fecha || null,
            };
          });
          
          // Apply documentos to all partidas in this declaration CRITICAL: Ensure partidas exist, create if they don't
          if (!updatedDeclaration.partidas || !Array.isArray(updatedDeclaration.partidas)) {
            updatedDeclaration.partidas = [];
          }
          
          // If no partidas exist, create at least one to apply documentos to
          if (updatedDeclaration.partidas.length === 0) {
            updatedDeclaration.partidas = [{}];
          }
          
          // Apply documentos to all partidas
          updatedDeclaration.partidas = updatedDeclaration.partidas.map((partida) => ({
            ...partida,
            documentos: [...templateDocumentos], // Overwrite with template documentos
          }));
        } else {
          // Template has no documentos - restore to original documentos
          if (updatedDeclaration.partidas && Array.isArray(updatedDeclaration.partidas)) {
            if (originalDeclaration && originalDeclaration.partidas && Array.isArray(originalDeclaration.partidas)) {
              // Restore each partida's documentos from original
              updatedDeclaration.partidas = updatedDeclaration.partidas.map((partida, partidaIndex) => ({
                ...partida,
                documentos: originalDeclaration.partidas[partidaIndex]?.documentos 
                  ? [...originalDeclaration.partidas[partidaIndex].documentos]
                  : [],
              }));
            } else {
              // No original data, clear all documentos
              updatedDeclaration.partidas = updatedDeclaration.partidas.map((partida) => ({
                ...partida,
                documentos: [],
              }));
            }
          }
        }
        
        // Handle item/partida fields: apply to all partidas NOTE: Exclude 'embalajes' and 'documentos' from this loop - they are handled separately
        if (selectedTemplate.data.partidas && typeof selectedTemplate.data.partidas === 'object' && !Array.isArray(selectedTemplate.data.partidas)) {
          // Template has item-level fields - apply them to all partidas
          if (updatedDeclaration.partidas && Array.isArray(updatedDeclaration.partidas)) {
            updatedDeclaration.partidas = updatedDeclaration.partidas.map((partida) => {
              const updatedPartida = { ...partida };
              // Apply each item field from template (exclude embalajes and documentos - handled separately)
              Object.keys(selectedTemplate.data.partidas).forEach((key) => {
                // Skip embalajes and documentos - they are handled in separate sections
                if (key === 'embalajes' || key === 'documentos') {
                  return;
                }
                if (selectedTemplate.data.partidas[key] !== null && selectedTemplate.data.partidas[key] !== undefined) {
                  updatedPartida[key] = selectedTemplate.data.partidas[key];
                }
              });
              return updatedPartida;
            });
          }
        }
        
        // Handle embalajes: merge template fields into existing embalajes Check both data.embalajes and data.partidas.embalajes for template embalajes
        let templateEmbalajesArray = null;
        if (selectedTemplate.data.embalajes && Array.isArray(selectedTemplate.data.embalajes) && selectedTemplate.data.embalajes.length > 0) {
          templateEmbalajesArray = selectedTemplate.data.embalajes;
        } else if (selectedTemplate.data.partidas && typeof selectedTemplate.data.partidas === 'object' && !Array.isArray(selectedTemplate.data.partidas)) {
          if (selectedTemplate.data.partidas.embalajes && Array.isArray(selectedTemplate.data.partidas.embalajes) && selectedTemplate.data.partidas.embalajes.length > 0) {
            templateEmbalajesArray = selectedTemplate.data.partidas.embalajes;
          }
        }
        
        if (templateEmbalajesArray && templateEmbalajesArray.length > 0) {
          // Template has embalajes - get the first template embalaje (fields to apply)
          const templateEmbalaje = templateEmbalajesArray[0];
          if (templateEmbalaje && typeof templateEmbalaje === 'object') {
            // Get all fields from template embalaje
            const templateFields = {};
            Object.keys(templateEmbalaje).forEach((key) => {
              if (templateEmbalaje[key] !== null && templateEmbalaje[key] !== undefined) {
                // Handle arrays (like marcas) by creating a deep copy
                if (Array.isArray(templateEmbalaje[key])) {
                  templateFields[key] = deepClone(templateEmbalaje[key]);
                } else {
                  templateFields[key] = templateEmbalaje[key];
                }
              }
            });
            
            // Apply template fields to existing embalajes in all partidas CRITICAL: Ensure partidas exist, create if they don't
            if (!updatedDeclaration.partidas || !Array.isArray(updatedDeclaration.partidas)) {
              updatedDeclaration.partidas = [];
            }
            
            // If no partidas exist, create at least one to apply embalajes to
            if (updatedDeclaration.partidas.length === 0) {
              updatedDeclaration.partidas = [{}];
            }
            
            updatedDeclaration.partidas = updatedDeclaration.partidas.map((partida, partidaIndex) => {
              // CRITICAL: Always use the CURRENT partida's embalajes first (these are the ones the user has) Only fallback to original if current partida has no embalajes at all
              let existingEmbalajes = null;
              
              // Priority 1: Use current partida's embalajes (these are the most up-to-date)
              if (partida.embalajes && Array.isArray(partida.embalajes) && partida.embalajes.length > 0) {
                // Deep copy to avoid mutations
                existingEmbalajes = deepClone(partida.embalajes);
              } 
              // Priority 2: Fallback to original declaration's embalajes only if current has none
              else if (originalDeclaration && 
                       originalDeclaration.partidas && 
                       Array.isArray(originalDeclaration.partidas) &&
                       originalDeclaration.partidas[partidaIndex] &&
                       originalDeclaration.partidas[partidaIndex].embalajes &&
                       Array.isArray(originalDeclaration.partidas[partidaIndex].embalajes) &&
                       originalDeclaration.partidas[partidaIndex].embalajes.length > 0) {
                // Fallback to original declaration's embalajes
                existingEmbalajes = deepClone(originalDeclaration.partidas[partidaIndex].embalajes);
              }
              
              // ALWAYS preserve ALL existing embalajes - merge template fields into EACH existing embalaje
              if (existingEmbalajes && existingEmbalajes.length > 0) {
                // Merge template fields into each existing embalaje (preserve all embalajes, update only template fields)
                const updatedEmbalajes = existingEmbalajes.map((existingEmbalaje) => {
                  // Create a deep copy of the existing embalaje to preserve ALL its fields
                  const updatedEmbalaje = deepClone(existingEmbalaje);
                  // Apply each template field to the existing embalaje (only update fields that are in the template)
                  Object.keys(templateFields).forEach((key) => {
                    // For marcas, replace the entire array For other fields, replace the value
                    updatedEmbalaje[key] = deepClone(templateFields[key]);
                  });
                  return updatedEmbalaje;
                });
                return {
                  ...partida,
                  embalajes: updatedEmbalajes, // Keep ALL existing embalajes (e.g., if 2 exist, keep both), just update the template fields
                };
              } else {
                // No existing embalajes at all, create new one from template
                const newEmbalaje = {};
                Object.keys(templateFields).forEach((key) => {
                  newEmbalaje[key] = deepClone(templateFields[key]);
                });
                return {
                  ...partida,
                  embalajes: [newEmbalaje],
                };
              }
            });
          }
        } else {
          // Template has no embalajes - restore to original embalajes
          if (updatedDeclaration.partidas && Array.isArray(updatedDeclaration.partidas)) {
            if (originalDeclaration && originalDeclaration.partidas && Array.isArray(originalDeclaration.partidas)) {
              // Restore each partida's embalajes from original
              updatedDeclaration.partidas = updatedDeclaration.partidas.map((partida, partidaIndex) => ({
                ...partida,
                embalajes: originalDeclaration.partidas[partidaIndex]?.embalajes 
                  ? deepClone(originalDeclaration.partidas[partidaIndex].embalajes)
                  : [],
              }));
            } else {
              // No original data, clear all embalajes
              updatedDeclaration.partidas = updatedDeclaration.partidas.map((partida) => ({
                ...partida,
                embalajes: [],
              }));
            }
          }
        }
        
        return updatedDeclaration;
      });
      
      // CRITICAL: Update editableData with the template-applied data Preserve array structure if original was array, otherwise return single object
      const finalData = Array.isArray(currentData) ? updatedData : (updatedData.length > 0 ? updatedData[0] : updatedData);
      
      // Validate that we have updated data
      if (!finalData || (Array.isArray(finalData) && finalData.length === 0)) {
        console.error('Template application resulted in empty data');
        return;
      }
      
      // CRITICAL: Update workflow session state (source of truth for workflows)
      if (activeWorkflowSessionId) {
        dispatch(updateWorkflowSessionState(activeWorkflowSessionId, {
          editableData: finalData,
        }));
      }
      
      // Also update global state for backward compatibility
      dispatch(setEditableData(finalData));
      
      // CRITICAL: Update active tab's editableData if we have an active tab
      if (activeTabId) {
        dispatch(updateTabEditableData(activeTabId, finalData));
        dispatch(updateTabIsDirty(activeTabId, true));
      }
      
      // Set isDirty for global (for backward compatibility)
      dispatch(setDeclarationDirty(true));
      
      // OPTIMIZED: editableData change will trigger validation via useEffect in useDeclarationSummary The validation is optimized to run quickly (100ms debounce) and will update errors in real-time
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate]); // Only depend on selectedTemplate to avoid infinite loops
  // Note: We intentionally don't include editableData/extractedData in deps to avoid loops The comparison check prevents re-applying the same template data

  // Open template modal automatically when extractedData is first set from API
  useEffect(() => {
    // Open modal if: extractedData exists, no template selected, modal closed, first time, and ProcessingModal closed
    if (extractedData && 
        (Array.isArray(extractedData) ? extractedData.length > 0 : true) &&
        !selectedTemplate && 
        !isTemplateModalOpen &&
        !hasOpenedTemplateModal) {
      
      // CRITICAL: Use ref to track retry count and prevent infinite loops
      let retryCount = 0;
      const maxRetries = 60; // Max 60 seconds wait
      let timeoutId = null;
      
      // Check if ProcessingModal is open - if so, wait and check again
      const checkProcessingModal = () => {
        // Clear any existing timeout
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        
        // Prevent infinite loop - stop after max retries
        if (retryCount >= maxRetries) {
          console.warn('[ReviewExtractedData] Max retries reached, opening template modal anyway');
          setIsTemplateModalOpen(true);
          setHasOpenedTemplateModal(true);
          return;
        }
        
        const isProcessingOpen = isAnyProcessingModalOpen();
        if (isProcessingOpen) {
          // ProcessingModal is open, check again in 1 second
          retryCount++;
          timeoutId = setTimeout(checkProcessingModal, 1000);
        } else {
          // ProcessingModal is closed, open template modal
          setIsTemplateModalOpen(true);
          setHasOpenedTemplateModal(true);
        }
      };
      
      // Small delay to ensure UI is ready, then check ProcessingModal status
      const timer = setTimeout(() => {
        checkProcessingModal();
      }, 500);
      
      return () => {
        clearTimeout(timer);
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    }
  }, [extractedData, selectedTemplate, isTemplateModalOpen, hasOpenedTemplateModal]);

  // Reset the flag when extractedData is cleared (new extraction session)
  useEffect(() => {
    if (!extractedData || (Array.isArray(extractedData) && extractedData.length === 0)) {
      setHasOpenedTemplateModal(false);
    }
  }, [extractedData]);

  // Single useEffect to handle all file loading - from Redux or IndexedDB
  useEffect(() => {
    const loadFiles = async () => {
      // First, check Redux state for files
      const reduxFiles = [cmrFile, ...(exportFiles || [])].filter(f => f && (f instanceof File || f instanceof Blob));
      
      if (reduxFiles.length > 0) {
        setFiles(reduxFiles);
        if (!selectedFile && reduxFiles[0]) {
          const fileUrl = URL.createObjectURL(reduxFiles[0]);
          setSelectedFile(reduxFiles[0]);
          setSelectedFileUrl(fileUrl);
        }
        return;
      }
      
      // PDFs are now stored only in memory (workflow file cache) - no localStorage/IndexedDB restore needed
    };
    
    loadFiles();
  }, [cmrFile, exportFiles, activeTabId, dispatch, selectedFile]);

  useEffect(() => {
    return () => {
      if (selectedFileUrl) {
        URL.revokeObjectURL(selectedFileUrl);
      }
    };
  }, [selectedFileUrl]);

  // Clear selected file if it was from fetched PDFs when split view closes
  useEffect(() => {
    if (selectedOption !== 'split' && selectedFile) {
      const wasFromFetched = fetchedPdfs.some(pdf => pdf.file === selectedFile);
      if (wasFromFetched) {
        setSelectedFile(null);
        if (selectedFileUrl) {
          URL.revokeObjectURL(selectedFileUrl);
          setSelectedFileUrl(null);
        }
      }
    }
  }, [selectedOption, selectedFile, selectedFileUrl, fetchedPdfs]);

  const closeModal = () => {
    setSelectedOption('text');
    scroller.scrollTo('review-extracted-data', {
      duration: 1500,
      delay: 20,
      offset: -24,
      smooth: 'easeInOutQuint',
    });
  };

  const handleFileClick = (file) => {
    if (!file) return;
    
    // Validate that file is a File or Blob object
    if (!(file instanceof File) && !(file instanceof Blob)) {
      console.warn('handleFileClick: Invalid file object', file);
      return;
    }
    
    if (selectedFileUrl) {
      URL.revokeObjectURL(selectedFileUrl);
    }

    try {
      const fileUrl = URL.createObjectURL(file);
      setSelectedFile(file);
      setSelectedFileUrl(fileUrl);
    } catch (error) {
      console.error('Failed to create object URL:', error, file);
    }
  };

  const handleTemplateSelect = (template) => {
    // Store the current template as previous before applying new one
    if (selectedTemplate) {
      sessionStorage.setItem('previousSelectedTemplate', JSON.stringify(selectedTemplate));
    } else {
      // If no template was selected before, store null to indicate no previous template
      sessionStorage.setItem('previousSelectedTemplate', JSON.stringify(null));
    }
    
    // Update the ref to track this template version
    lastAppliedTemplateRef.current = template;
    
    // Mark that we've applied a template (prevents preferred template from overriding user selection)
    if (template) {
      hasAppliedPreferredTemplateRef.current = true;
    }
    
    // CRITICAL: Save selectedTemplate to active workflow session state (per-workflow)
    if (activeWorkflowSessionId) {
      dispatch(updateWorkflowSessionState(activeWorkflowSessionId, {
        selectedTemplate: template,
      }));
    }
    
    // Also update global state for backward compatibility (template CSS class uses this)
    dispatch(setSelectedTemplate(template));
    
    // Apply template data to editableData (preserves expediente values)
    const currentData = editableData || extractedData;
    
    if (currentData && template?.data) {
      // Get original data from active tab (source of truth) NOTE: originalExtractedData is deprecated - use workflow tabs instead
      let originalData = null;
      try {
        // Use active tab's editableData as original data
        if (activeTabData && activeTabData.editableData) {
          originalData = activeTabData.editableData;
        } else if (editableData) {
          originalData = editableData;
        }
      } catch (e) {
        console.error('Error getting original data from tab:', e);
      }
      
      // CRITICAL: Handle both array and single object cases - ensure we always have an array Validate that we have data to apply template to
      if (!currentData) {
        console.warn('No data available to apply template to');
        return;
      }
      
      const dataArray = Array.isArray(currentData) ? currentData : [currentData];
      const originalDataArray = originalData 
        ? (Array.isArray(originalData) ? originalData : [originalData])
        : null;
      
      // CRITICAL: Ensure we have at least one declaration to apply template to
      if (dataArray.length === 0) {
        console.warn('No declarations found to apply template to');
        return;
      }
      
      const updatedData = dataArray.map((declaration, index) => {
            // Map template data to declaration structure, preserving expediente
            const updatedDeclaration = { ...declaration };
            
            // Find the original declaration by expediente or by index if expediente doesn't match
            let originalDeclaration = null;
            if (originalDataArray) {
              // First try to match by expediente
              originalDeclaration = originalDataArray.find(d => 
                d.expediente === declaration.expediente
              );
              // If no match by expediente, try by index
              if (!originalDeclaration && originalDataArray[index]) {
                originalDeclaration = originalDataArray[index];
              }
            }
            
            // Define all template-applicable fields Note: marcas is NOT included here - it should only be in embalajes, not at declaration level Start with hardcoded fields for backward compatibility and special handling
            const templateFields = {
              // Declaration type and dates
              tipo: template.data.tipo,
              fecha_registro: template.data.fecha_registro,
              fecha_creacion: template.data.fecha_creacion,
              fecha_operacion: template.data.fecha_operacion,
              mrn: template.data.mrn,
              
              // Declaration identification
              declaracion_1: template.data.declaracion_1,
              declaracion_2: template.data.declaracion_2,
              declaracion_3: template.data.declaracion_3,
              
              // Operators/Parties
              cliente_id: template.data.cliente_id,
              remitente_id: template.data.remitente_id,
              clave_declarante: template.data.clave_declarante,
              
              // Customs information
              aduana_entrada: template.data.aduana_entrada,
              aduana_salida: template.data.aduana_salida,
              aduana_despacho: template.data.aduana_despacho,
              
              // Transport information
              modo_transporte_frontera: template.data.modo_transporte_frontera,
              modo_transporte_interior: template.data.modo_transporte_interior,
              medio_transporte_frontera: template.data.medio_transporte || template.data.medio_transporte_frontera,
              identidad_transporte_frontera: template.data.identidad_transporte_frontera,
              
              // Country information
              pais_destino: template.data.pais_destino,
              pais_exportacion: template.data.pais_exportacion,
              pais_transporte_frontera: template.data.pais_transporte_frontera,
              pais_ubicacion: template.data.pais_ubicacion,
              
              // Location information
              localizacion: template.data.localizacion || template.data.localizacion_mercancia,
              tipo_ubicacion: template.data.tipo_ubicacion,
              provincia: template.data.provincia,
              codigo_un: template.data.codigo_un,
              lugar_entrega: template.data.lugar_entrega,
              tipo_autorizacion_cd: template.data.tipo_autorizacion_cd,
              identificacion_adicional: template.data.identificacion_adicional,
              
              // Currency information
              divisa: template.data.divisa,
              cambio_divisa: template.data.cambio_divisa,
              divisa_iso: template.data.divisa_iso,
              
              // Commercial information - normalize so we never store [0]
              tiene_contenedores: normalizeBinary(template.data.tiene_contenedores),
              total_bultos: template.data.total_bultos,
              factura: template.data.factura,
              fecha_factura: template.data.fecha_factura,
              condiciones_entrega: template.data.condiciones_entrega,
              naturaleza_transaccion: template.data.naturaleza_transaccion,
              
              // Contact information
              contacto_telefono: template.data.contacto_telefono,
              e_mail: template.data.e_mail,
              
              // Status and flags
              autorizacion_por_operacion: template.data.autorizacion_por_operacion,
              flag_datos_seguridad: template.data.flag_datos_seguridad,
              es_simplificado: template.data.es_simplificado,
              es_recapitulativo: template.data.es_recapitulativo,
              estado_pdc: template.data.estado_pdc,
              estado_edi: template.data.estado_edi,
              estado_edi_canarias: template.data.estado_edi_canarias,
              completo: template.data.completo,
              es_plantilla: template.data.es_plantilla,
              
              // Additional information
              observaciones: template.data.observaciones,
              
              // Item-level fields (regimen_solicitud is actually item-level, but kept for backward compatibility)
              regimen_solicitud: template.data.regimen_solicitud,
            };
            
            // CRITICAL FIX: Dynamically add ALL other fields from template.data that aren't in the hardcoded list This ensures fields like 'telefono', 'contacto', 'medio_transporte', etc. are applied Exclude special nested structures: documentos, partidas, embalajes
            const excludedKeys = ['documentos', 'partidas', 'embalajes', 'marcas'];
            Object.keys(template.data).forEach((key) => {
              // Skip if already in templateFields or is a special nested structure
              if (!excludedKeys.includes(key) && templateFields[key] === undefined) {
                templateFields[key] = template.data[key];
              }
            });
            
            // Helper function to validate and normalize numeric/boolean fields
            const normalizeNumericField = (value, fieldName, defaultValue) => {
              // If value is an array, null, or undefined, use default
              if (Array.isArray(value) || value === null || value === undefined || value === '') {
                return defaultValue;
              }
              // Convert to number if it's a valid number
              const numValue = Number(value);
              if (!isNaN(numValue) && isFinite(numValue)) {
                return numValue;
              }
              // If not a valid number, use default
              return defaultValue;
            };

            // Apply template fields: ALWAYS overwrite if exists in template, restore to original if not
            Object.keys(templateFields).forEach((key) => {
              // CRITICAL: Check if field exists in template.data (including null values - null is a valid value to apply) Only exclude undefined (field doesn't exist in template)
              const hasValueInTemplate = template.data.hasOwnProperty(key) && template.data[key] !== undefined;
              
              if (hasValueInTemplate) {
                // Field exists in template - ALWAYS overwrite with validation for numeric fields
                let valueToApply = templateFields[key];
                
                // Convert numeric fields using convertFieldValue (handles tipo and all other numeric fields)
                if (NUMERIC_FIELDS.has(key)) {
                  valueToApply = convertFieldValue(key, valueToApply);
                } else {
                  // Fallback to normalizeNumericField for legacy fields (can be removed later)
                if (key === 'flag_datos_seguridad') {
                  valueToApply = normalizeNumericField(valueToApply, key, 0);
                } else if (key === 'completo') {
                  valueToApply = normalizeNumericField(valueToApply, key, 1);
                } else if (key === 'es_plantilla') {
                  valueToApply = normalizeNumericField(valueToApply, key, 0);
                } else if (key === 'es_simplificado' || key === 'es_recapitulativo' || key === 'autorizacion_por_operacion') {
                  valueToApply = normalizeNumericField(valueToApply, key, 0);
                  }
                }
                
                // ALWAYS overwrite - don't check if it already exists
                updatedDeclaration[key] = valueToApply;
              } else {
                // Field doesn't exist in template - restore to original value or clear
                const templateSpecificFields = [
                  // Declaration type and dates
                  'tipo', 'fecha_registro', 'fecha_creacion', 'fecha_operacion', 'mrn',
                  // Declaration identification
                  'declaracion_1', 'declaracion_2', 'declaracion_3',
                  // Operators/Parties
                  'cliente_id', 'remitente_id', 'clave_declarante',
                  // Customs information
                  'aduana_entrada', 'aduana_salida', 'aduana_despacho',
                  // Transport information
                  'modo_transporte_frontera', 'modo_transporte_interior', 'medio_transporte_frontera', 'identidad_transporte_frontera',
                  // Country information
                  'pais_destino', 'pais_exportacion', 'pais_transporte_frontera', 'pais_ubicacion',
                  // Location information
                  'tipo_ubicacion', 'localizacion', 'provincia', 'codigo_un', 'lugar_entrega', 'tipo_autorizacion_cd', 'identificacion_adicional',
                  // Currency information
                  'divisa', 'cambio_divisa', 'divisa_iso',
                  // Commercial information
                  'tiene_contenedores', 'total_bultos', 'factura', 'fecha_factura', 'condiciones_entrega', 'naturaleza_transaccion',
                  // Contact information
                  'contacto_telefono', 'e_mail',
                  // Status and flags
                  'autorizacion_por_operacion', 'flag_datos_seguridad', 'es_simplificado', 'es_recapitulativo', 'estado_pdc', 'estado_edi', 'estado_edi_canarias', 'completo', 'es_plantilla',
                  // Additional information
                  'observaciones',
                  // Item-level (kept for backward compatibility)
                  'regimen_solicitud',
                ];
                if (templateSpecificFields.includes(key)) {
                  // Restore to original value from before templates were applied
                  if (originalDeclaration && originalDeclaration[key] !== undefined) {
                    // Also validate original value for numeric fields
                    if (key === 'flag_datos_seguridad') {
                      updatedDeclaration[key] = normalizeNumericField(originalDeclaration[key], key, 0);
                    } else if (key === 'completo') {
                      updatedDeclaration[key] = normalizeNumericField(originalDeclaration[key], key, 1);
                    } else if (key === 'es_plantilla') {
                      updatedDeclaration[key] = normalizeNumericField(originalDeclaration[key], key, 0);
                    } else {
                      updatedDeclaration[key] = originalDeclaration[key];
                    }
                  } else {
                    // Set default values for numeric fields, empty string for others
                    if (key === 'flag_datos_seguridad') {
                      updatedDeclaration[key] = 0;
                    } else if (key === 'completo') {
                      updatedDeclaration[key] = 1;
                    } else if (key === 'es_plantilla') {
                      updatedDeclaration[key] = 0;
                    } else {
                      updatedDeclaration[key] = '';
                    }
                  }
                }
              }
            });
            
            // Handle documentos: add to partidas level. tipo categories: 1=Autorizaciones, 2=Suplementarios, 3=Referencias, 4=Transporte
            if (template.data.documentos && Array.isArray(template.data.documentos) && template.data.documentos.length > 0) {
              // Template has documentos - apply them to all partidas
              const templateDocumentos = template.data.documentos.map((doc) => {
                // Handle backward compatibility: old templates might have tipo as string (e.g., 'N380') New format: { codigo_documento: 'N380', tipo: 2 } Old format: { tipo: 'N380', descripcion: '...' }
                let codigoDoc = '';
                let tipoNum = 2; // Default: Documentos Suplementarios
                
                if (doc.codigo_documento) {
                  // New format: codigo_documento exists
                  codigoDoc = doc.codigo_documento;
                  tipoNum = (doc.tipo !== undefined && doc.tipo !== null && typeof doc.tipo === 'number') ? doc.tipo : 2;
                } else if (doc.tipo && typeof doc.tipo === 'string') {
                  // Old format: tipo is a string like 'N380', 'N730', etc.
                  codigoDoc = doc.tipo;
                  // Map old tipo string to numeric tipo
                  if (codigoDoc === 'N730') {
                    tipoNum = 4; // Documentos de Transporte
                  } else if (codigoDoc === 'Y024') {
                    tipoNum = 3; // Referencias Adicionales
                  } else {
                    tipoNum = 2; // Documentos Suplementarios (N380, N003, 1217, 1009, I5007, etc.)
                  }
                } else if (doc.tipo && typeof doc.tipo === 'number') {
                  // If tipo is already a number, use it (but codigo_documento should exist)
                  tipoNum = doc.tipo;
                }
                
                return {
                  codigo_documento: codigoDoc,
                  tipo: tipoNum,
                  numero: doc.numero || '',
                  fecha: doc.fecha || null,
                };
              });
              
              // Apply documentos to all partidas (ensure partidas exist, create if needed)
              if (!updatedDeclaration.partidas || !Array.isArray(updatedDeclaration.partidas)) {
                updatedDeclaration.partidas = [];
              }
              
              // If no partidas exist, create at least one to apply documentos to
              if (updatedDeclaration.partidas.length === 0) {
                updatedDeclaration.partidas = [{}];
              }
              
              // Apply documentos to all partidas
              updatedDeclaration.partidas = updatedDeclaration.partidas.map((partida) => ({
                ...partida,
                documentos: [...templateDocumentos], // Overwrite with template documentos
              }));
            } else {
              // Template has no documentos - restore to original documentos from before any templates were applied
              if (updatedDeclaration.partidas && Array.isArray(updatedDeclaration.partidas)) {
                if (originalDeclaration && originalDeclaration.partidas && Array.isArray(originalDeclaration.partidas)) {
                  // Restore each partida's documentos from original
                  updatedDeclaration.partidas = updatedDeclaration.partidas.map((partida, partidaIndex) => ({
                    ...partida,
                    documentos: originalDeclaration.partidas[partidaIndex]?.documentos 
                      ? [...originalDeclaration.partidas[partidaIndex].documentos]
                      : [],
                  }));
                } else {
                  // No original data, clear all documentos
                  updatedDeclaration.partidas = updatedDeclaration.partidas.map((partida) => ({
                    ...partida,
                    documentos: [],
                  }));
                }
              }
            }
            
            // Handle item/partida fields: apply to all partidas NOTE: Exclude 'embalajes' and 'documentos' from this loop - they are handled separately
            if (template.data.partidas && typeof template.data.partidas === 'object' && !Array.isArray(template.data.partidas)) {
              // Template has item-level fields - apply them to all partidas
              if (updatedDeclaration.partidas && Array.isArray(updatedDeclaration.partidas)) {
                updatedDeclaration.partidas = updatedDeclaration.partidas.map((partida) => {
                  const updatedPartida = { ...partida };
                  // Apply each item field from template (exclude embalajes and documentos - handled separately)
                  Object.keys(template.data.partidas).forEach((key) => {
                    // Skip embalajes and documentos - they are handled in separate sections
                    if (key === 'embalajes' || key === 'documentos') {
                      return;
                    }
                    if (template.data.partidas[key] !== null && template.data.partidas[key] !== undefined) {
                      updatedPartida[key] = template.data.partidas[key];
                    }
                  });
                  return updatedPartida;
                });
              }
            }
            
            // Handle embalajes: merge template fields into existing embalajes (check data.embalajes and data.partidas.embalajes)
            let templateEmbalajesArray = null;
            if (template.data.embalajes && Array.isArray(template.data.embalajes) && template.data.embalajes.length > 0) {
              templateEmbalajesArray = template.data.embalajes;
            } else if (template.data.partidas && typeof template.data.partidas === 'object' && !Array.isArray(template.data.partidas)) {
              if (template.data.partidas.embalajes && Array.isArray(template.data.partidas.embalajes) && template.data.partidas.embalajes.length > 0) {
                templateEmbalajesArray = template.data.partidas.embalajes;
              }
            }
            
            if (templateEmbalajesArray && templateEmbalajesArray.length > 0) {
              // Template has embalajes - get the first template embalaje (fields to apply)
              const templateEmbalaje = templateEmbalajesArray[0];
              if (templateEmbalaje && typeof templateEmbalaje === 'object') {
                // Get all fields from template embalaje
                const templateFields = {};
                Object.keys(templateEmbalaje).forEach((key) => {
                  if (templateEmbalaje[key] !== null && templateEmbalaje[key] !== undefined) {
                    // Handle arrays (like marcas) by creating a deep copy
                    if (Array.isArray(templateEmbalaje[key])) {
                      templateFields[key] = deepClone(templateEmbalaje[key]);
                    } else {
                      templateFields[key] = templateEmbalaje[key];
                    }
                  }
                });
                
                // Apply template fields to existing embalajes in all partidas CRITICAL: Ensure partidas exist, create if they don't
                if (!updatedDeclaration.partidas || !Array.isArray(updatedDeclaration.partidas)) {
                  updatedDeclaration.partidas = [];
                }
                
                // If no partidas exist, create at least one to apply embalajes to
                if (updatedDeclaration.partidas.length === 0) {
                  updatedDeclaration.partidas = [{}];
                }
                
                updatedDeclaration.partidas = updatedDeclaration.partidas.map((partida, partidaIndex) => {
                  // Use current partida's embalajes first, fallback to original if none exist
                  let existingEmbalajes = null;
                  if (partida.embalajes && Array.isArray(partida.embalajes) && partida.embalajes.length > 0) {
                    existingEmbalajes = deepClone(partida.embalajes);
                  } else if (originalDeclaration?.partidas?.[partidaIndex]?.embalajes?.length > 0) {
                    existingEmbalajes = deepClone(originalDeclaration.partidas[partidaIndex].embalajes);
                  }
                  
                  // Preserve all existing embalajes - merge template fields into each
                  if (existingEmbalajes && existingEmbalajes.length > 0) {
                    // Merge template fields into each existing embalaje (preserve all embalajes, update only template fields)
                    const updatedEmbalajes = existingEmbalajes.map((existingEmbalaje) => {
                      // Create a deep copy of the existing embalaje to preserve ALL its fields
                      const updatedEmbalaje = deepClone(existingEmbalaje);
                      // Apply each template field to the existing embalaje (only update fields that are in the template)
                      Object.keys(templateFields).forEach((key) => {
                        // For marcas, replace the entire array For other fields, replace the value
                        updatedEmbalaje[key] = deepClone(templateFields[key]);
                      });
                      return updatedEmbalaje;
                    });
                    return {
                      ...partida,
                      embalajes: updatedEmbalajes, // Keep ALL existing embalajes (e.g., if 2 exist, keep both), just update the template fields
                    };
                  } else {
                    // No existing embalajes at all, create new one from template
                    const newEmbalaje = {};
                    Object.keys(templateFields).forEach((key) => {
                      newEmbalaje[key] = deepClone(templateFields[key]);
                    });
                    return {
                      ...partida,
                      embalajes: [newEmbalaje],
                    };
                  }
                });
              }
            } else {
              // Template has no embalajes - restore to original embalajes
              if (updatedDeclaration.partidas && Array.isArray(updatedDeclaration.partidas)) {
                if (originalDeclaration && originalDeclaration.partidas && Array.isArray(originalDeclaration.partidas)) {
                  // Restore each partida's embalajes from original
                  updatedDeclaration.partidas = updatedDeclaration.partidas.map((partida, partidaIndex) => ({
                    ...partida,
                    embalajes: originalDeclaration.partidas[partidaIndex]?.embalajes 
                      ? deepClone(originalDeclaration.partidas[partidaIndex].embalajes)
                      : [],
                  }));
                } else {
                  // No original data, clear all embalajes
                  updatedDeclaration.partidas = updatedDeclaration.partidas.map((partida) => ({
                    ...partida,
                    embalajes: [],
                  }));
                }
              }
            }
            
            return updatedDeclaration;
          });
      
      // CRITICAL: Update editableData with the template-applied data Preserve array structure if original was array, otherwise return single object
      const finalData = Array.isArray(currentData) ? updatedData : (updatedData.length > 0 ? updatedData[0] : updatedData);
      
      // Validate that we have updated data
      if (!finalData || (Array.isArray(finalData) && finalData.length === 0)) {
        console.error('Template application resulted in empty data');
        return;
      }
      
      // CRITICAL: Update workflow session state (source of truth for workflows)
      if (activeWorkflowSessionId) {
        dispatch(updateWorkflowSessionState(activeWorkflowSessionId, {
          editableData: finalData,
        }));
      }
      
      // Also update global state for backward compatibility
      dispatch(setEditableData(finalData));
      
      // CRITICAL: Update active tab's editableData if we have an active tab
      if (activeTabId) {
        dispatch(updateTabEditableData(activeTabId, finalData));
        dispatch(updateTabIsDirty(activeTabId, true));
      }
      
      // Set isDirty for global (for backward compatibility)
      dispatch(setDeclarationDirty(true));
      setIsDirty(true);
      
      // OPTIMIZED: Trigger validation immediately after template application editableData change will trigger validation via useEffect, but we ensure it happens
    }
  };

  return (
    <div className="mt-8" id="review-extracted-data" data-nav-breadcrumb="Review Details">
      {selectedOption === 'split' && !isActiveWorkflowProcessingBusy && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-999 py-2 px-3 h-screen flex flex-col gap-2">
          <div
            className="shadow-md flex absolute z-[999] bottom-5 items-center gap-2 p-2 px-4 rounded-full right-7 bg-red-50 cursor-pointer"
            onClick={closeModal}
          >
            <X className="size-4 mt-[2px] stroke-red-500" />
            <p className="text-sm text-red-500">{getReviewExtractedDataTranslation('close', isSpanish)}</p>
          </div>
          
          <div className="flex items-center justify-center gap-2 flex-1">
            <div className="bg-white p-4 shadow-lg min-w-[50%] min-h-[100%] overflow-y-auto flex-1 flex flex-col rounded-lg">
              <div className='flex items-center justify-between gap-2 mb-1 flex-wrap'>
              <h2 className="text-lg font-bold truncate" title='Uploaded Files'>
                {getReviewExtractedDataTranslation('uploadedFiles', isSpanish)} ({files.length + fetchedPdfs.length})
              </h2>
            <div className='relative text-xs'>
                <button className={` w-fit flex gap-2 justify-between items-center`}  onClick={() => setDropdownOpen(!dropdownOpen)}>
                    {selectedFile ? 
                    <div
                    className={`truncate border-container px-2 rounded-full cursor-pointer`}
                  >
                    <div className="p-2 px-1 flex gap-2 items-center">
                      <div className="flex gap-1 items-center">
                        <img src={pdfIcon} className="h-4 w-4" alt="PDF" />
                        <p className="truncate max-w-xs">{selectedFile.name}</p>
                      </div>
                      <ChevronDown className={`w-4 h-4 ${dropdownOpen && "rotate-180"}`} />
                    </div>
                  </div>:  <div
                    className={`truncate border-container px-2 rounded-full cursor-pointer`}
                  >
                    <div className="p-2 px-1 flex gap-2 items-center">
                      <div className="flex gap-1 items-center">
                        <img src={pdfIcon} className="h-4 w-4" alt="PDF" />
                        <p className="truncate max-w-xs">
                          {isLoadingPdfs 
                            ? getReviewExtractedDataTranslation('loadingPdfs', isSpanish) || 'Loading...'
                            : getReviewExtractedDataTranslation('noPdfFileSelected', isSpanish)}
                        </p>
                      </div>
                      <ChevronDown className={`w-4 h-4 ${dropdownOpen && "rotate-180"}`} />
                    </div>
                  </div>}
                    
                    
                </button>
                {dropdownOpen && (
                  <div className='right-0 bg-white shadow-md p-2 border-container rounded-md absolute mt-1  max-h-96 overflow-auto'>
                    {isLoadingPdfs ? (
                      <div className="bg-gray-100 p-4 rounded text-center text-gray-500">
                        {getReviewExtractedDataTranslation('loadingPdfs', isSpanish) || 'Loading PDFs...'}
                      </div>
                    ) : (files.length > 0 || fetchedPdfs.length > 0) ? (
                      <div className="flex text-xs gap-1.5 flex-col py-1">
                        {/* Show local files first */}
                        {files.map((file, index) => (
                          <div
                            key={`local-${index}`}
                            className={` truncate border-container px-2 rounded-full cursor-pointer ${
                              selectedFile === file ? 'bg-secondary text-white !border-secondary' : ''
                            }`}
                            onClick={() => handleFileClick(file)}
                          >
                            <div className="p-2 px-1">
                              <div className="flex gap-1 items-center">
                                <img src={pdfIcon} className="h-4 w-4" alt="PDF" />
                                <p className="truncate max-w-xs">{file.name}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                        {/* Show fetched PDFs from API */}
                        {fetchedPdfs.map((pdfData) => (
                          <div
                            key={`fetched-${pdfData.id}`}
                            className={` truncate border-container px-2 rounded-full cursor-pointer ${
                              selectedFile === pdfData.file ? 'bg-secondary text-white !border-secondary' : ''
                            }`}
                            onClick={() => handleFileClick(pdfData.file)}
                          >
                            <div className="p-2 px-1">
                              <div className="flex gap-1 items-center">
                                <img src={pdfIcon} className="h-4 w-4" alt="PDF" />
                                <p className="truncate max-w-xs">{pdfData.file.name}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="bg-gray-100 p-4 rounded text-center text-gray-500">
                        {getReviewExtractedDataTranslation('noFilesAvailable', isSpanish)}
                      </div>
                    )}

                    </div>
                )}
            </div>
            </div>

              <div className="pdf-viewer flex flex-1 w-full rounded-lg overflow-hidden mt-2">
                {selectedFileUrl ? (
                  <PdfViewer 
                    selectedFileUrl={selectedFileUrl} 
                    fileName={selectedFile?.name || ""}
                  />
                ) : (
                  <div className="min-w-full bg-gray-100 p-8 rounded-lg flex justify-center items-center text-gray-500">
                    {getReviewExtractedDataTranslation('selectAFileToView', isSpanish)}
                  </div>
                )}
              </div>
            </div>
            <div className="bg-white shadow-lg min-w-[50%] min-h-[100%] max-h-[94vh] overflow-hidden flex-1 rounded-lg">
              <div className="p-4 pt-0 overflow-auto split-declaration-height pb-16">
                  <DeclarationSummary
                  selectedOption={selectedOption}
                    jsonData={extractedData}
                    isSummaryExpanded={isSummaryExpanded}
                    setIsSummaryExpanded={setIsSummaryExpanded}
                    isDirty={isDirty}
                    setIsDirty={setIsDirty}
                  />
              </div>
            </div>
          </div>
        </div>
      )}
      <div className='flex gap-1 items-center mb-2 justify-between'>
        <h1 className='text-xl truncate'>{getReviewExtractedDataTranslation('extractedData', isSpanish)}</h1>
      
      <div className="flex gap-1 items-center">
        <button
          className={`flex items-center gap-1 py-2 px-2${
            selectedOption === 'split' ? 'bg-secondary' : ''
          } rounded-full`}
          title="Expand"
          onClick={() => {
            setIsSummaryExpanded(!isSummaryExpanded);
          }}
        >
            {isSummaryExpanded ? <ChevronsDownUp className="size-4" /> : <ChevronsUpDown className="size-4" />}
          
          <p className='text-sm'>{isSummaryExpanded ? getReviewExtractedDataTranslation('collapse', isSpanish) : getReviewExtractedDataTranslation('expand', isSpanish)}</p>
          
        </button>
        {!isActiveWorkflowProcessingBusy && (
        <button
          className={`flex items-center gap-2 py-2 bg-secondary px-4${
            selectedOption === 'split' ? 'bg-secondary' : ''
          } rounded-full`}
          title="Split View"
          onClick={() => {
            setSelectedOption('split');
            setIsSummaryExpanded(true);
          }}
        >
            
          <Columns2 className="size-5" />
          <p className='text-sm truncate'>{getReviewExtractedDataTranslation('splitView', isSpanish)}</p>
          
        </button>
        )}
        <button
          className="svg-force-white flex items-center gap-4 py-2 px-4 rounded-full template-buttona bg-primary"
          title="Template"
          onClick={() => {
            setIsTemplateModalOpen(true);
          }}
        >
          <div className='flex items-center gap-2'>
          <FileText className="size-5" />
          <p className='text-sm uppercase truncate'>{selectedTemplate ? selectedTemplate.title : getReviewExtractedDataTranslation('selectTemplate', isSpanish)}</p>
          </div>
          <ChevronDown className={`w-4 h-4 ${dropdownOpen && "rotate-180"}`} />
        </button>
      </div>
      </div>
      <div className="border-container p-4 pt-0 rounded-xl">
          <DeclarationSummary
            jsonData={extractedData}
            isSummaryExpanded={isSummaryExpanded}
            setIsSummaryExpanded={setIsSummaryExpanded}
            isDirty={isDirty}
            setIsDirty={setIsDirty}
          />
      </div>
      <TemplateModal
        templates={templates}
        isOpen={isTemplateModalOpen}
        onClose={() => setIsTemplateModalOpen(false)}
        onSelect={handleTemplateSelect}
        selectedTemplate={selectedTemplate}
        title={getReviewExtractedDataTranslation('selectTemplateTitle', isSpanish)}
        isSpanish={isSpanish}
      />
    </div>
  );
};